/**
 * Landing query for the Areas tab.
 *
 * For every `:Area` it returns:
 *   - `area`             — the area node itself
 *   - `parent`           — the immediate `(:Area)-[:PART_OF]->(:Area)`
 *                          parent, if any
 *   - `site`             — the closest `(:Site)` anchor reached via
 *                          `PART_OF*1..` (areas hanging off the
 *                          organization root have no anchor and fall
 *                          into the "strays" bucket client-side)
 *   - `subAreaCount`     — direct `<-[:PART_OF]-(:Area)` children
 *   - `equipmentCount`   — distinct `:Equipment` `LOCATED_IN` this
 *                          area OR any descendant (via
 *                          `PART_OF*0..` collected as `subtree`)
 *   - `processCount`     — same shape, for `:Process` nodes
 *   - `partCount`        — same shape, for `:Part` nodes
 *
 * Drained client-side via `drainCypherRows` so the view gets every
 * area rather than the proxy's first page.
 */
export const PLANT_AREAS_CYPHER = `
            MATCH (a:Area)
            OPTIONAL MATCH (a)-[:PART_OF]->(parent:Area)
            OPTIONAL MATCH (a)-[:PART_OF*1..]->(site:Site)
            WITH a, parent, head(collect(distinct site)) AS site
            OPTIONAL MATCH (a)<-[:PART_OF]-(sub:Area)
            WITH a, parent, site, count(distinct sub) AS subAreaCount
            OPTIONAL MATCH (descendant:Area)-[:PART_OF*0..]->(a)
            WITH a, parent, site, subAreaCount, collect(distinct descendant) AS subtree
            OPTIONAL MATCH (eq:Equipment)-[:LOCATED_IN]->(scope:Area)
              WHERE scope IN subtree
            WITH a, parent, site, subAreaCount, subtree,
                 count(distinct eq) AS equipmentCount
            OPTIONAL MATCH (proc:Process)-[:LOCATED_IN]->(scope:Area)
              WHERE scope IN subtree
            WITH a, parent, site, subAreaCount, subtree, equipmentCount,
                 count(distinct proc) AS processCount
            OPTIONAL MATCH (part:Part)-[:LOCATED_IN]->(scope:Area)
              WHERE scope IN subtree
            WITH a, parent, site, subAreaCount, equipmentCount, processCount,
                 count(distinct part) AS partCount
            RETURN a AS area,
                   parent,
                   site,
                   subAreaCount,
                   equipmentCount,
                   processCount,
                   partCount
            ORDER BY toLower(coalesce(a.name, a.id))
            SKIP toInteger($skip) LIMIT toInteger($pageSize)
          `
